import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import { Schemas } from '~/api/gen'
import { Divider } from '~/components/elements/Divider'
import { NoResults } from '~/components/elements/NoResults'
import { Pagination } from '~/components/modules/Pagination/Pagination'
import { usePaginationProps } from '~/hooks/usePaginationProps'
import { Folders, FolderItem } from '../shared/folder/components/Folders'
import { FrequentlyUsedCoordinateItems } from '../shared/memberDetail/components/FrequentlyUsedCoordinateItems'
import { FrequentlyUsedTags } from '../shared/memberDetail/components/FrequentlyUsedTags'
import { MemberDetailLayout } from '../shared/memberDetail/components/MemberDetailLayout'
import { FETCH_COUNT_PER_PAGE } from './constants'

export type Props = {
  member: Schemas.member_large_detail
  folderCollection: Schemas.get_members_user_name_folders_v1_response
}

export const MemberFolder: NextPage<Props> = ({ member, folderCollection }) => {
  const router = useRouter()
  const currentPage = Number(router.query.pageno) || 1
  const frequentlyUsedTagsExist = member.frequently_used_tags.length > 0
  const frequentlyUsedCoordinateItemsExist =
    member.frequently_used_coordinate_items.length > 0
  const paginationProps = usePaginationProps(
    currentPage,
    Math.ceil(
      Math.min(
        folderCollection.folder_total_count,
        folderCollection.folder_max_count
      ) / FETCH_COUNT_PER_PAGE
    ),
    false
  )

  const isNoResults = folderCollection.folder_total_count === 0

  return (
    <MemberDetailLayout
      pageType="folders"
      member={member}
      currentPage={currentPage}
    >
      {!isNoResults ? (
        <>
          <div className="p-3 xl:p-0 xl:pt-[18px]">
            <Folders>
              {folderCollection.folders.map(
                (folder: Schemas.folder_collection_with_folder_elements, i) => (
                  <li
                    key={folder.id}
                    className="xl:rounded-[4px] xl:border xl:border-gray-300 xl:bg-white"
                  >
                    <FolderItem
                      userName={member.user_name}
                      folder={folder}
                      imagePriority={i < 4}
                    />
                  </li>
                )
              )}
            </Folders>
          </div>
          <div className="pt-2 xl:pt-[70px]">
            <Pagination {...paginationProps} />
          </div>
          {(frequentlyUsedTagsExist || frequentlyUsedCoordinateItemsExist) && (
            <>
              <div
                className={`hidden xl:block ${isNoResults ? 'xl:pt-10' : 'xl:pt-7'}`}
              >
                <Divider />
              </div>
              <section className="grid gap-y-[22px] px-4 pt-10 xl:gap-y-7 xl:px-0 xl:pt-[30px]">
                {frequentlyUsedTagsExist && (
                  <FrequentlyUsedTags
                    nickName={member.nick_name}
                    frequentlyUsedTags={member.frequently_used_tags}
                  />
                )}
                {frequentlyUsedCoordinateItemsExist && (
                  <FrequentlyUsedCoordinateItems
                    nickName={member.nick_name}
                    frequentlyUsedCoordinateItems={
                      member.frequently_used_coordinate_items
                    }
                  />
                )}
              </section>
            </>
          )}
        </>
      ) : (
        <NoResults
          className="pb-[38px] pt-[66px] xl:pb-[118px] xl:pt-[120px]"
          type="FOLDER"
        >
          公開されている
          <br />
          お気に入りはありません
        </NoResults>
      )}
    </MemberDetailLayout>
  )
}
